/**
 * An analysis's one status, in business words — the single place this is
 * decided, used by the Analyses list, the Analysis page and the "Choose
 * analyses" wizard step alike (docs/ui-design.md §19: "One
 * analyses list per audit ... each analysis has one status").
 *
 * Pure and framework-free on purpose: this is the one thing in the feature
 * worth unit-testing directly, and a function with no React in it is the
 * cheapest way to keep it that way. Tones are the same five words
 * `shared/ui.tsx`'s `StatusBadge` already renders (`good`/`warn`/`bad`/
 * `busy`/`neutral`) so every badge in the app still shares one palette.
 */
import { count } from '../../utils/format';

export type AnalysisStatusTone = 'good' | 'warn' | 'bad' | 'busy' | 'neutral';

/** The tones, mapped onto the badge variants `base.css` defines — the same
 *  mapping `shared/ui.tsx`'s `StatusBadge` uses. Shared with the report's
 *  frozen analysis badge so there is one palette for analysis statuses. */
export const TONE_TO_BADGE: Record<AnalysisStatusTone, string> = {
	good: 'clean',
	warn: 'warning',
	bad: 'exception',
	busy: 'info',
	neutral: 'gray',
};

/** A glyph in front of the word, for surfaces that want one (the wizard's
 *  "✓ Ready" / "⚠ Blocked"). Only the tones the wizard can show have one. */
export const TONE_GLYPH: Partial<Record<AnalysisStatusTone, string>> = {
	good: '✓',
	warn: '⚠',
};

export interface AnalysisStatusPresentation {
	label: string;
	tone: AnalysisStatusTone;
}

/**
 * The fields this reasons over, lifted out of `AnalysisOverviewRow` rather
 * than importing it: the wizard's availability-preview rows (before an audit
 * exists, nothing has a `status` at all) and the full overview rows both
 * satisfy this, and neither has to be padded with fields it does not have.
 */
export interface AnalysisStatusInput {
	/** Absent for a wizard "Choose analyses" row — nothing has run yet, and
	 *  there is no `AuditAnalysis` to carry a status on. */
	status?: 'NOT_RUN' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';
	/** Null = not checked yet (no data scope). */
	availability?: 'READY' | 'BLOCKED' | null;
	outdated?: boolean;
	/** Null = not calculated (disabled / not implemented / legacy). */
	exceptionFindings?: number | null;
}

/**
 * One row's status, as a label and a tone.
 *
 * Blocked wins over everything except an in-flight run: a result that is
 * stale, failed or sitting there `NOT_RUN` is still, first and foremost,
 * blocked if the current data scope cannot feed it. A row with no `status` at
 * all is the wizard's own preview — read as "Ready" rather than "Not run",
 * because nothing has been created to "not run" yet.
 */
export function analysisStatusPresentation(
	row: AnalysisStatusInput,
): AnalysisStatusPresentation {
	const running = row.status === 'RUNNING' || row.status === 'QUEUED';

	if (row.availability === 'BLOCKED' && !running) {
		return { label: 'Blocked', tone: 'warn' };
	}

	if (row.status === undefined) {
		return { label: 'Ready', tone: 'good' };
	}

	switch (row.status) {
		case 'QUEUED':
			return { label: 'Waiting', tone: 'neutral' };
		case 'RUNNING':
			return { label: 'Running', tone: 'busy' };
		case 'FAILED':
			return { label: 'Failed', tone: 'bad' };
		case 'NOT_RUN':
			return { label: 'Not run', tone: 'neutral' };
		case 'COMPLETED': {
			if (row.outdated) return { label: 'Out of date', tone: 'warn' };
			const findings = row.exceptionFindings ?? null;
			if (findings === null) return { label: 'Completed', tone: 'good' };
			if (findings === 0) return { label: 'No findings', tone: 'good' };
			return {
				label: `${count(findings)} finding${findings === 1 ? '' : 's'}`,
				tone: 'warn',
			};
		}
		default:
			return { label: 'Not run', tone: 'neutral' };
	}
}

/** "Started 42s ago" / "Started 1m 23s ago" — no percentages, ever (docs/
 *  ui-ux spec §7). Recomputed at render time; the caller re-renders this on
 *  every poll, which is what keeps it moving. */
export function startedAgoLabel(
	startedAt: string | null | undefined,
): string | null {
	if (!startedAt) return null;
	const startedMs = new Date(startedAt).getTime();
	if (!Number.isFinite(startedMs)) return null;
	const elapsed = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
	const minutes = Math.floor(elapsed / 60);
	const seconds = elapsed % 60;
	return `Started ${minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`} ago`;
}

/** "Needs: LR number, Transporter name" — the blocked reason's short form for
 *  a table cell, where the full sentence in 08-table-ux.md's example does not
 *  fit. */
export function missingColumnsShort(
	missingColumns: Array<{ key: string; label: string }>,
): string | null {
	if (missingColumns.length === 0) return null;
	return `Needs: ${missingColumns.map((column) => column.label).join(', ')}`;
}
